import React from 'react';
import StatusBadge from '../StatusBadge';
import { TYPE, SURFACE, FOCUS } from '../ui/tokens';

/**
 * The table the copilot's answers actually needed.
 *
 * Collections used to render as a stack of cards, one per record, each a title over a pre-joined
 * subtitle string ("ABC Foods • $18.40 • 120 units"). Nothing in that stack lines up, so comparing
 * ten prices meant reading ten sentences. A table is not a nicer way to show the same rows; it is
 * what makes them comparable.
 *
 * Alignment carries meaning here, so it is not a styling choice:
 *   - text reads from the left,
 *   - money, quantities and counts are right-aligned with tabular figures, so the decimal points
 *     line up and a column can be scanned for magnitude without reading a single number.
 *
 * MONEY ARRIVES PRE-FORMATTED. The server owns money display (utils/moneyDisplay.js) precisely so
 * that a currency is never invented and two currencies are never merged — an amount reaches here
 * as "TTD 1,918.50" or "1918.50 (currency not recorded)". This component aligns it and never
 * re-formats it; re-formatting on the client is how the two would drift apart.
 */

export type ColumnType = 'text' | 'money' | 'number' | 'date' | 'status';

export type Column = {
  key: string;
  label: string;
  type?: ColumnType;
  /** Override the alignment implied by `type`. */
  align?: 'left' | 'right';
  /** Cap the rendered width so one long product name cannot blow out every other column. */
  maxWidth?: string;
};

export type DataTableProps = {
  columns: Column[];
  rows: Array<Record<string, unknown>>;
  /** The true total behind the page, when more rows exist than are shown. */
  total?: number | null;
  caption?: string;
  onRowClick?: (row: Record<string, unknown>) => void;
  /** Rows for which `onRowClick` would do nothing render as plain rows. Default: every row clicks. */
  isRowClickable?: (row: Record<string, unknown>) => boolean;
  emptyLabel?: string;
};

/** Numbers sit right; everything else reads left. */
function alignOf(column: Column): 'left' | 'right' {
  if (column.align) return column.align;
  return column.type === 'money' || column.type === 'number' ? 'right' : 'left';
}

function renderCell(value: unknown, column: Column) {
  if (value === null || value === undefined || value === '') {
    // An absent value is stated, not left blank — a blank cell reads as zero.
    return <span className="text-slate-400">--</span>;
  }
  if (column.type === 'status') return <StatusBadge status={String(value)} />;
  return String(value);
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  total = null,
  caption,
  onRowClick,
  isRowClickable,
  emptyLabel = 'No rows.',
}) => {
  if (!columns.length) return null;
  if (!rows.length) return <p className={`${TYPE.meta} text-slate-500`}>{emptyLabel}</p>;

  const shown = rows.length;
  const knownTotal = typeof total === 'number' && total > 0 ? total : null;
  const isTruncated = knownTotal !== null && knownTotal > shown;

  return (
    <div className="w-full">
      {/* The table scrolls inside its OWN container. A wide table must never make the page body
          scroll sideways, which is what happens when the overflow is left to an ancestor. */}
      <div className={`w-full overflow-x-auto ${SURFACE.line} border rounded-lg`}>
        <table className="w-full border-collapse text-left">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="bg-slate-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  // Sticky so the header survives a long vertical scroll — a column of numbers
                  // whose heading has scrolled away is a column of unlabelled numbers.
                  className={[
                    'sticky top-0 z-10 bg-slate-50 border-b border-slate-200',
                    'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600',
                    'whitespace-nowrap',
                    alignOf(column) === 'right' ? 'text-right' : 'text-left',
                  ].join(' ')}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const clickable = Boolean(onRowClick) && (isRowClickable ? isRowClickable(row) : true);
              return (
                <tr
                  key={String(row.id ?? rowIndex)}
                  className={[
                    'border-b border-slate-100 last:border-b-0',
                    clickable ? `cursor-pointer hover:bg-primary-50 ${FOCUS}` : '',
                  ].join(' ')}
                  {...(clickable
                    ? {
                      tabIndex: 0,
                      role: 'button',
                      onClick: () => onRowClick?.(row),
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick?.(row);
                        }
                      },
                    }
                    : {})}
                >
                  {columns.map((column) => {
                    const value = row[column.key];
                    const right = alignOf(column) === 'right';
                    return (
                      <td
                        key={column.key}
                        // A 120-character product name truncates with the full text on hover,
                        // rather than forcing every other column down to nothing.
                        title={value == null ? undefined : String(value)}
                        className={[
                          'px-3 py-2 align-top text-slate-800',
                          TYPE.cell,
                          right ? `text-right ${TYPE.figure}` : 'text-left',
                          column.type === 'status' ? '' : 'truncate',
                        ].join(' ')}
                        style={{ maxWidth: column.maxWidth ?? (right ? '10rem' : '18rem') }}
                      >
                        {renderCell(value, column)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* A page presented as the whole set is a wrong answer, not a small omission. */}
      <p className={`${TYPE.meta} text-slate-500 mt-1.5`}>
        {isTruncated ? `Showing ${shown} of ${knownTotal}` : `${shown} ${shown === 1 ? 'row' : 'rows'}`}
      </p>
    </div>
  );
};

export default DataTable;
